'use client';

import { c as _c } from "react/compiler-runtime";
import { Tooltip } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgChevrondown, SvgHelpQuestionFill, SvgShare2, SvgStarFill } from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { UnauthQueryOptions } from '@/api';
import { openShareDrawer } from '@/app/_components/GlobalModal/_components/ShareDrawer';
import { useScrollToSection } from '@/app/donation/_hooks/useScrollToSection';
import { CartCount } from '@/components/molecule/CartCount';
import { useClosableOnClickOpeningState } from '@/hooks/useClosableOnClickOpeningState';
import { useAuthStore } from '@/stores/AuthStore';
import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { PriceSection } from '../../../_components/PriceSection';
import type { MarketProduct, ProductOption, SelectedOption } from '../types';
import { CouponDialogContent } from './CouponDialogContent';
import { DonationGiftBanner } from './DonationGiftBanner';
import { LoginPromptModal } from './LoginPromptModal';
interface ProductInfoProps {
  product: MarketProduct;
  productId: string;
  selectedOptions: SelectedOption[];
  totalAmount: number;
  onSelectOption: (option: ProductOption) => void;
  onQuantityChange: (optionId: string, quantity: number) => void;
  onRemoveOption: (optionId: string) => void;
  onAddToCart?: () => void;
  isAddingToCart?: boolean;
  onBuyNow?: () => void;
  isBuyingNow?: boolean;
  isPreparing?: boolean;
  preparingLabel?: string;
}
export const ProductInfo = t0 => {
  const $ = _c(146);
  const {
    product,
    productId,
    selectedOptions,
    totalAmount,
    onSelectOption,
    onQuantityChange,
    onRemoveOption,
    onAddToCart,
    isAddingToCart,
    onBuyNow,
    isBuyingNow,
    isPreparing,
    preparingLabel: t1
  } = t0;
  const preparingLabel = t1 === undefined ? "\uC0C1\uD488 \uC900\uBE44\uC911\uC774\uC5D0\uC694" : t1;
  const {
    openingState: t2,
    setClosableOnClick
  } = useClosableOnClickOpeningState();
  const [isOptionOpen, setIsOptionOpen] = t2;
  const [isBenefitOpen, setIsBenefitOpen] = useState(false);
  const [loginPromptDescription, setLoginPromptDescription] = useState(null);
  const isLogin = useAuthStore(_temp);
  const {
    scrollToSectionWithTab
  } = useScrollToSection();
  const {
    toast
  } = useToast();
  let t3;
  if ($[0] !== productId) {
    t3 = UnauthQueryOptions["get:/wello/v2/allows/local-market/products/{productId}/reviews"]({
      path: {
        productId
      },
      query: {
        page: 1,
        size: 1,
        sort: "LATEST"
      }
    });
    $[0] = productId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    data: reviewData
  } = useQuery(t3);
  const reviewSummary = reviewData?.context?.summary;
  const averageRating = reviewSummary?.averageRating ?? 0;
  const totalReviewCount = reviewSummary?.totalCount ?? 0;
  const hasDiscount = product.expectedFinalPrice !== product.regularPrice;
  const totalDiscount = product.regularPrice - product.expectedFinalPrice;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("flex flex-col gap-40 py-40", "mobile-tablet:py-0");
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("flex items-start justify-between", "mobile-tablet:px-20");
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== product.govCodeName) {
    t6 = <span className="bg-Gray-200 px-12 py-4 font-600 text-Gray-600 typo-body4 mobile-tablet:px-6 mobile-tablet:py-[3px] mobile-tablet:typo-caption2">{product.govCodeName}</span>;
    $[4] = product.govCodeName;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== product.vendorName) {
    t7 = <span className="font-600 text-Gray-600 typo-body3 mobile-tablet:typo-caption1">{product.vendorName}</span>;
    $[6] = product.vendorName;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <div className="flex items-center gap-8">{t6}{t7}</div>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t10;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("flex flex-col gap-12", "mobile-tablet:gap-4");
    t10 = cn("line-clamp-3 font-600 text-Gray-900 typo-h4", "mobile-tablet:typo-h6");
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  let t11;
  if ($[13] !== product.productName) {
    t11 = <h1 className={t10}>{product.productName}</h1>;
    $[13] = product.productName;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SvgStarFill className="size-24 text-O-400 mobile-tablet:size-16" />;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== averageRating) {
    t13 = <span className="font-600 text-Gray-900 typo-body3">{averageRating}</span>;
    $[16] = averageRating;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== scrollToSectionWithTab) {
    t14 = () => scrollToSectionWithTab("review", {
      offset: 18
    });
    $[18] = scrollToSectionWithTab;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t14 || $[21] !== totalReviewCount) {
    t15 = <button type="button" className="font-600 text-Gray-600 underline typo-body3" onClick={t14}>{totalReviewCount}건 후기 보기</button>;
    $[20] = t14;
    $[21] = totalReviewCount;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t13 || $[24] !== t15) {
    t16 = <div className="flex items-center gap-8">{t12}{t13}{t15}</div>;
    $[23] = t13;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t11 || $[27] !== t16) {
    t17 = <div className={t9}>{t11}{t16}</div>;
    $[26] = t11;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t17 || $[30] !== t8) {
    t18 = <div className="flex flex-1 flex-col gap-12 mobile-tablet:gap-20">{t8}{t17}</div>;
    $[29] = t17;
    $[30] = t8;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <button type="button" aria-label={"\uACF5\uC720\uD558\uAE30"} className="shrink-0 p-4 text-Gray-700" onClick={_temp2}><SvgShare2 className="size-20 text-Gray-700" /></button>;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t18) {
    t20 = <div className={t5}>{t18}{t19}</div>;
    $[33] = t18;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== hasDiscount || $[36] !== product.expectedFinalPrice || $[37] !== product.regularPrice) {
    t21 = hasDiscount ? [{
      amount: product.regularPrice,
      description: "\uC77C\uBC18\uAC00",
      color: "blue"
    }, {
      amount: product.expectedFinalPrice,
      description: "\uCD5C\uB300 \uD560\uC778\uAC00",
      color: "black"
    }] : [{
      amount: product.regularPrice,
      description: "\uC77C\uBC18\uAC00",
      color: "black"
    }];
    $[35] = hasDiscount;
    $[36] = product.expectedFinalPrice;
    $[37] = product.regularPrice;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = hasDiscount ? isBenefitOpen : undefined;
  let t23;
  if ($[39] !== hasDiscount) {
    t23 = hasDiscount ? () => setIsBenefitOpen(_temp3) : undefined;
    $[39] = hasDiscount;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== hasDiscount || $[42] !== product.couponDiscount || $[43] !== product.productDiscount) {
    t24 = hasDiscount ? [...(product.productDiscount ? [{
      label: "\uC0C1\uD488 \uCD94\uAC00\uD560\uC778",
      value: `-${product.productDiscount.discountAmount.toLocaleString()}원`
    }] : []), ...(product.couponDiscount ? [{
      label: product.couponDiscount.couponTitle,
      value: `-${product.couponDiscount.discountValue.toLocaleString()}원`
    }] : [])] : undefined;
    $[41] = hasDiscount;
    $[42] = product.couponDiscount;
    $[43] = product.productDiscount;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== hasDiscount || $[46] !== totalDiscount) {
    t25 = hasDiscount ? `-${totalDiscount.toLocaleString()}원` : undefined;
    $[45] = hasDiscount;
    $[46] = totalDiscount;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== hasDiscount || $[49] !== isLogin || $[50] !== isPreparing || $[51] !== product.couponAvailable || $[52] !== product.mallProductId) {
    t26 = hasDiscount && product.couponAvailable ? isPreparing ? <span className="shrink-0 bg-Gray-100 px-16 py-8 font-700 text-Gray-400 typo-body4 mobile-tablet:typo-caption1">쿠폰받기</span> : <button type="button" className="shrink-0 bg-O-400 px-16 py-8 font-700 text-Gray-white typo-body4 mobile-tablet:typo-caption1" onClick={() => {
      if (!isLogin) {
        setLoginPromptDescription("\uB85C\uADF8\uC778 \uD6C4 \uCFE0\uD3F0\uC744 \uBC1B\uC744 \uC218 \uC788\uC5B4\uC694");
        return;
      }
      openResponsiveDialog({
        component: CouponDialogContent,
        props: {
          productId: product.mallProductId
        },
        modalHeader: "\uCFE0\uD3F0 \uBC1B\uAE30",
        drawerType: "bottom"
      });
    }}>쿠폰받기</button> : undefined;
    $[48] = hasDiscount;
    $[49] = isLogin;
    $[50] = isPreparing;
    $[51] = product.couponAvailable;
    $[52] = product.mallProductId;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const t27 = Math.max(product.reviewTextPoint, product.reviewPhotoPoint);
  let t28;
  if ($[54] !== t27) {
    t28 = t27.toLocaleString();
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t28) {
    t29 = <p className="font-500 text-Gray-900 typo-body4">최대{" "}<span className="font-700 text-O-400">{t28}P</span></p>;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span className="border border-O-400 px-6 py-2 font-700 text-O-400 typo-caption2">웰포인트 적립</span>;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t29) {
    t31 = <div className="flex items-center gap-4">{t29}{t30}</div>;
    $[59] = t29;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== product.reviewTextPoint) {
    t32 = product.reviewTextPoint.toLocaleString();
    $[61] = product.reviewTextPoint;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== t32) {
    t33 = <span>일반 후기 작성 시{" "}<span className="font-600">{t32}P</span>{" "}적립</span>;
    $[63] = t32;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] !== product.reviewPhotoPoint) {
    t34 = product.reviewPhotoPoint.toLocaleString();
    $[65] = product.reviewPhotoPoint;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] !== t34) {
    t35 = <span>포토 후기 작성 시{" "}<span className="font-600">{t34}P</span>{" "}추가 적립</span>;
    $[67] = t34;
    $[68] = t35;
  } else {
    t35 = $[68];
  }
  let t36;
  if ($[69] !== t33 || $[70] !== t35) {
    t36 = <div className="flex flex-col text-Gray-900 typo-caption1">{t33}{t35}</div>;
    $[69] = t33;
    $[70] = t35;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== t31 || $[73] !== t36) {
    t37 = {
      label: "\uAD6C\uB9E4 \uD61C\uD0DD",
      content: <div className="flex flex-col gap-2">{t31}{t36}</div>
    };
    $[72] = t31;
    $[73] = t36;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <span>배송비</span>;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  let t39;
  if ($[76] !== product.deliveryFee || $[77] !== product.freeShippingAmount || $[78] !== product.freeShippingAvailable) {
    t39 = product.deliveryFee === 0 ? "\uC774 \uC0C1\uD488\uC740 \uBB34\uB8CC\uBC30\uC1A1 \uC0C1\uD488\uC785\uB2C8\uB2E4." : product.freeShippingAvailable ? `${product.freeShippingAmount.toLocaleString()}원 이상 구매 시 무료배송` : "\uBC30\uC1A1\uBE44\uB294 \uD310\uB9E4\uCC98\uBCC4\uB85C \uBD80\uACFC\uB418\uBA70, \uC8FC\uBB38 \uC2DC \uD569\uC0B0\uB429\uB2C8\uB2E4.";
    $[76] = product.deliveryFee;
    $[77] = product.freeShippingAmount;
    $[78] = product.freeShippingAvailable;
    $[79] = t39;
  } else {
    t39 = $[79];
  }
  let t40;
  if ($[80] !== t39) {
    t40 = <div className="typo-body3 mobile-tablet:typo-caption1">{t39}</div>;
    $[80] = t39;
    $[81] = t40;
  } else {
    t40 = $[81];
  }
  let t41;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <SvgHelpQuestionFill className="size-14 text-Gray-400" />;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  let t42;
  if ($[83] !== t40) {
    t42 = <div className="flex items-center gap-4">{t38}<Tooltip theme="outlined" side="bottom" align="start" className="max-w-[25rem]" label={t40}>{t41}</Tooltip></div>;
    $[83] = t40;
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  let t43;
  if ($[85] !== product.deliveryFee) {
    t43 = product.deliveryFee === 0 ? "\uBB34\uB8CC\uBC30\uC1A1" : `${product.deliveryFee.toLocaleString()}원`;
    $[85] = product.deliveryFee;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== t43) {
    t44 = <span className="font-500 text-Gray-900 typo-body4">{t43}</span>;
    $[87] = t43;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  let t45;
  if ($[89] !== t42 || $[90] !== t44) {
    t45 = {
      label: t42,
      content: t44
    };
    $[89] = t42;
    $[90] = t44;
    $[91] = t45;
  } else {
    t45 = $[91];
  }
  let t46;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = {
      label: "\uBC30\uC1A1 \uC548\uB0B4",
      content: <div className="flex flex-col gap-2 typo-body4"><span className="font-500 text-Gray-900">2~7일 예상</span><span className="font-400 text-Gray-900 typo-caption1">(식품의 경우 지연 발생 시 1주~2주 소요)</span></div>
    };
    $[92] = t46;
  } else {
    t46 = $[92];
  }
  let t47;
  if ($[93] !== t37 || $[94] !== t45) {
    t47 = [t37, t45, t46];
    $[93] = t37;
    $[94] = t45;
    $[95] = t47;
  } else {
    t47 = $[95];
  }
  let t48;
  if ($[96] !== t21 || $[97] !== t22 || $[98] !== t23 || $[99] !== t24 || $[100] !== t25 || $[101] !== t26 || $[102] !== t47) {
    t48 = <PriceSection prices={t21} isBenefitOpen={t22} onBenefitToggle={t23} benefitItems={t24} maxDiscountAmount={t25} couponButton={t26} infos={t47} />;
    $[96] = t21;
    $[97] = t22;
    $[98] = t23;
    $[99] = t24;
    $[100] = t25;
    $[101] = t26;
    $[102] = t47;
    $[103] = t48;
  } else {
    t48 = $[103];
  }
  let t49;
  if ($[104] !== t20 || $[105] !== t48) {
    t49 = <div className="flex flex-col gap-24"><div className="flex flex-col gap-28 mobile-tablet:gap-12">{t20}{t48}</div></div>;
    $[104] = t20;
    $[105] = t48;
    $[106] = t49;
  } else {
    t49 = $[106];
  }
  let t50;
  if ($[107] !== isOptionOpen || $[108] !== isPreparing || $[109] !== onQuantityChange || $[110] !== onRemoveOption || $[111] !== onSelectOption || $[112] !== product.options || $[113] !== selectedOptions || $[114] !== setClosableOnClick || $[115] !== setIsOptionOpen || $[116] !== toast) {
    t50 = !isPreparing && <div className={cn("flex flex-col gap-8", "mobile-tablet:hidden")}><div className="relative" onMouseEnter={() => setClosableOnClick(false)} onMouseLeave={() => setClosableOnClick(true)}><button type="button" className="flex h-48 w-full items-center justify-between border border-Gray-300 bg-Gray-white px-16" onClick={() => setIsOptionOpen(!isOptionOpen)}><span className="font-400 text-Gray-600 typo-body3">옵션을 선택해주세요</span><SvgChevrondown className={cn("size-20 text-Gray-600 transition-transform", isOptionOpen && "rotate-180")} /></button>{isOptionOpen && <ul className="absolute inset-x-0 top-full z-10 mt-4 overflow-hidden border border-Gray-300 bg-Gray-white shadow-md">{product.options.map(option => <li key={option.id}><button type="button" className={cn("flex w-full items-center justify-between px-16 py-12", option.soldOutYN ? "cursor-not-allowed bg-Gray-50" : "hover:bg-Gray-50")} onClick={() => {
              if (option.soldOutYN) {
                toast({
                  message: "\uD488\uC808\uB41C \uC0C1\uD488\uC785\uB2C8\uB2E4",
                  type: "fail"
                });
                return;
              }
              onSelectOption(option);
              setIsOptionOpen(false);
            }}><span className={cn("font-400 typo-body3", option.soldOutYN ? "text-Gray-400" : "text-Gray-900")}>{option.name}</span><span className={cn("font-600 typo-body3", option.soldOutYN ? "text-Gray-400" : "text-Gray-900")}>{option.extraPrice ? `+${option.extraPrice.toLocaleString()}원` : `${option.price.toLocaleString()}원`}</span></button></li>)}</ul>}</div>{selectedOptions.length > 0 && <div className="flex flex-col gap-8">{selectedOptions.map(option_0 => <div key={option_0.optionId} className="flex flex-col gap-8 bg-Gray-50 p-12"><p className="font-400 text-Gray-600 typo-caption1">{option_0.name}</p><div className="flex items-center justify-between"><CartCount count={option_0.quantity} onCountChange={count => onQuantityChange(option_0.optionId, count)} onDelete={() => onRemoveOption(option_0.optionId)} /><span className="font-600 text-Gray-900 typo-body3">{(option_0.price * option_0.quantity).toLocaleString()}원</span></div></div>)}</div>}</div>;
    $[107] = isOptionOpen;
    $[108] = isPreparing;
    $[109] = onQuantityChange;
    $[110] = onRemoveOption;
    $[111] = onSelectOption;
    $[112] = product.options;
    $[113] = selectedOptions;
    $[114] = setClosableOnClick;
    $[115] = setIsOptionOpen;
    $[116] = toast;
    $[117] = t50;
  } else {
    t50 = $[117];
  }
  let t51;
  if ($[118] !== t49 || $[119] !== t50) {
    t51 = <div className="flex flex-col gap-28 mobile-tablet:gap-12">{t49}{t50}</div>;
    $[118] = t49;
    $[119] = t50;
    $[120] = t51;
  } else {
    t51 = $[120];
  }
  let t52;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = cn("flex flex-col gap-12", "mobile-tablet:hidden");
    $[121] = t52;
  } else {
    t52 = $[121];
  }
  let t53;
  if ($[122] !== isAddingToCart || $[123] !== isBuyingNow || $[124] !== isLogin || $[125] !== isPreparing || $[126] !== onAddToCart || $[127] !== onBuyNow || $[128] !== preparingLabel || $[129] !== selectedOptions || $[130] !== toast || $[131] !== totalAmount) {
    t53 = <div className="flex flex-col gap-20">{isPreparing ? <button type="button" disabled={true} className="flex h-68 w-full items-center justify-center bg-Gray-100 font-700 text-Gray-400 typo-h6">{preparingLabel}</button> : <><div className="flex items-center justify-between"><span className="font-700 text-Gray-900 typo-h6">선택 금액</span><span className="font-800 text-Gray-900 typo-h4">{totalAmount.toLocaleString()}원</span></div><div className="flex gap-12"><button type="button" className="flex h-68 flex-1 items-center justify-center border border-O-400 font-700 text-O-400 typo-h6 disabled:opacity-50" onClick={() => {
            if (!isLogin) {
              setLoginPromptDescription("\uB85C\uADF8\uC778 \uD6C4 \uC7A5\uBC14\uAD6C\uB2C8\uC5D0 \uB2F4\uC744 \uC218 \uC788\uC5B4\uC694");
              return;
            }
            onAddToCart?.();
          }} disabled={isAddingToCart || selectedOptions.length === 0}>장바구니 담기</button><button type="button" className="flex h-68 flex-1 items-center justify-center bg-O-400 font-700 text-Gray-white typo-h6 disabled:opacity-50" disabled={isBuyingNow || selectedOptions.length === 0} onClick={() => {
            if (selectedOptions.length === 0) {
              toast({
                message: "\uC635\uC158\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694",
                type: "fail"
              });
              return;
            }
            if (!isLogin) {
              setLoginPromptDescription("\uB85C\uADF8\uC778 \uD6C4 \uAD6C\uB9E4\uD560 \uC218 \uC788\uC5B4\uC694");
              return;
            }
            onBuyNow?.();
          }}>구매하기</button></div></>}</div>;
    $[122] = isAddingToCart;
    $[123] = isBuyingNow;
    $[124] = isLogin;
    $[125] = isPreparing;
    $[126] = onAddToCart;
    $[127] = onBuyNow;
    $[128] = preparingLabel;
    $[129] = selectedOptions;
    $[130] = toast;
    $[131] = totalAmount;
    $[132] = t53;
  } else {
    t53 = $[132];
  }
  let t54;
  if ($[133] !== product) {
    t54 = <DonationGiftBanner product={product} />;
    $[133] = product;
    $[134] = t54;
  } else {
    t54 = $[134];
  }
  let t55;
  if ($[135] !== t53 || $[136] !== t54) {
    t55 = <div className={t52}>{t53}{t54}</div>;
    $[135] = t53;
    $[136] = t54;
    $[137] = t55;
  } else {
    t55 = $[137];
  }
  const t56 = loginPromptDescription !== null;
  let t57;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = () => setLoginPromptDescription(null);
    $[138] = t57;
  } else {
    t57 = $[138];
  }
  const t58 = loginPromptDescription ?? "";
  let t59;
  if ($[139] !== t56 || $[140] !== t58) {
    t59 = <LoginPromptModal opened={t56} onClose={t57} description={t58} />;
    $[139] = t56;
    $[140] = t58;
    $[141] = t59;
  } else {
    t59 = $[141];
  }
  let t60;
  if ($[142] !== t51 || $[143] !== t55 || $[144] !== t59) {
    t60 = <div className={t4}>{t51}{t55}{t59}</div>;
    $[142] = t51;
    $[143] = t55;
    $[144] = t59;
    $[145] = t60;
  } else {
    t60 = $[145];
  }
  return t60;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2() {
  return openShareDrawer(location.href);
}
function _temp3(prev) {
  return !prev;
}